import { defineAsyncComponent } from 'vue';
import { registerModuleComponents } from '@/modules/loader';

// Third-party components — keep eager (used on nearly every page)
import InfiniteLoading from './InfiniteLoading.vue';

// Base components — keep eager (used on every CRUD page)
import BaseList from './base/BaseList.vue';
import BaseTable from './base/BaseTable.vue';
import BasePreview from './base/BasePreview.vue';
import BaseOverview from './base/BaseOverview.vue';
import BaseFields from './base/BaseFields.vue';
import FieldLabel from './base/FieldLabel.vue';
import SectionIcon from './base/SectionIcon.vue';
import ListRecordLink from './base/ListRecordLink.vue';
import AttentionList from './base/AttentionList.vue';
import EmployeeAutocomplete from './forms/EmployeeAutocomplete.vue';
import AppTaginput from './forms/AppTaginput.vue';

// Frequently used components — keep eager
import AppAvatar from './Avatar.vue';
import BackButton from './BackButton.vue';
import DocumentLinesTable from './DocumentLinesTable.vue';
import AppEmpty from './Empty.vue';
import HelpTooltip from './HelpTooltip.vue';
import MdiIcon from './MdiIcon.vue';
import PdfButton from './PdfButton.vue';
import ProductLink from './ProductLink.vue';
import ResizableColumns from './ResizableColumns.vue';
import AppNotfound from './NotFound.vue';
import SmartDropdown from './SmartDropdown.vue';

export function registerComponents(app) {
    // Third-party (eager)
    app.component('InfiniteLoading', InfiniteLoading);

    // Base components (eager)
    app.component('base-list', BaseList);
    app.component('base-table', BaseTable);
    app.component('base-preview', BasePreview);
    app.component('base-overview', BaseOverview);
    app.component('base-fields', BaseFields);
    app.component('field-label', FieldLabel);
    app.component('section-icon', SectionIcon);
    app.component('list-record-link', ListRecordLink);
    app.component('attention-list', AttentionList);

    // Frequently used (eager)
    app.component('app-avatar', AppAvatar);
    app.component('back-button', BackButton);
    app.component('document-lines-table', DocumentLinesTable);
    app.component('app-empty', AppEmpty);
    app.component('help-tooltip', HelpTooltip);
    app.component('mdi-icon', MdiIcon);
    app.component('pdf-button', PdfButton);
    app.component('product-link', ProductLink);
    app.component('resizable-columns', ResizableColumns);
    app.component('app-notfound', AppNotfound);
    app.component('smart-dropdown', SmartDropdown);

    // Lazy-loaded platform components — entity-agnostic, used across all modules
    app.component(
        'draggable',
        defineAsyncComponent(() => import('sortablejs-vue3').then(m => m.Sortable))
    );
    app.component(
        'vue-markdown',
        defineAsyncComponent(() => import('./VueMarkdown.vue'))
    );
    // A length of work typed as « 2h30 » or « 1j », bound as minutes.
    app.component(
        'duration-input',
        defineAsyncComponent(() => import('./forms/DurationInput.vue'))
    );
    app.component(
        'app-tree',
        defineAsyncComponent(() => import('./tree/TreeView.vue'))
    );
    app.component(
        'app-activities',
        defineAsyncComponent(() => import('./Activities.vue'))
    );
    app.component(
        'app-comments',
        defineAsyncComponent(() => import('./Comments.vue'))
    );
    app.component(
        'app-notification',
        defineAsyncComponent(() => import('./Notification.vue'))
    );
    app.component(
        'app-pictures',
        defineAsyncComponent(() => import('./Pictures.vue'))
    );
    // Any file on any record — `app-pictures` is the image strip, this is the
    // flat list a record's attachments actually are.
    app.component(
        'app-attachments',
        defineAsyncComponent(() => import('./Attachments.vue'))
    );
    app.component(
        'photo-gallery',
        defineAsyncComponent(() => import('./jobs/PhotoGallery.vue'))
    );
    app.component(
        'app-documents',
        defineAsyncComponent(() => import('./Documents.vue'))
    );
    app.component(
        'app-document-preview',
        defineAsyncComponent(() => import('./DocumentPreview.vue'))
    );
    app.component(
        'app-linked-documents',
        defineAsyncComponent(() => import('./LinkedDocuments.vue'))
    );
    app.component(
        'app-shortcuts',
        defineAsyncComponent(() => import('./Shortcuts.vue'))
    );
    app.component(
        'app-logs',
        defineAsyncComponent(() => import('./logs/Logs.vue'))
    );
    app.component(
        'app-logs-content-detail',
        defineAsyncComponent(() => import('./logs/Detail.vue'))
    );
    app.component(
        'app-log-field-changes',
        defineAsyncComponent(() => import('./logs/FieldChanges.vue'))
    );
    app.component(
        'app-logs-content-address',
        defineAsyncComponent(() => import('./logs/Address.vue'))
    );
    app.component(
        'app-logs-content-quote',
        defineAsyncComponent(() => import('./logs/QuoteLogDetail.vue'))
    );
    app.component(
        'app-contact',
        defineAsyncComponent(() => import('./combos/Contact.vue'))
    );
    app.component(
        'app-country',
        defineAsyncComponent(() => import('./combos/Country.vue'))
    );
    app.component(
        'app-project',
        defineAsyncComponent(() => import('./combos/Project.vue'))
    );
    app.component(
        'app-product',
        defineAsyncComponent(() => import('./combos/Product.vue'))
    );
    app.component(
        'app-helper',
        defineAsyncComponent(() => import('./Helper.vue'))
    );
    app.component(
        'google-autocomplete',
        defineAsyncComponent(() => import('./GoogleAutocomplete.vue'))
    );
    app.component(
        'chart-js',
        defineAsyncComponent(() => import('./ChartJs.vue'))
    );

    // Form components (lazy)
    app.component(
        'app-datepicker',
        defineAsyncComponent(() => import('./forms/AppDatepicker.vue'))
    );
    app.component(
        'app-datetimepicker',
        defineAsyncComponent(() => import('./forms/AppDatetimepicker.vue'))
    );
    app.component(
        'app-duration-input',
        defineAsyncComponent(() => import('./forms/AppDurationInput.vue'))
    );
    app.component(
        'dynamic-form',
        defineAsyncComponent(() => import('./forms/DynamicForm.vue'))
    );
    app.component(
        'field-repeater',
        defineAsyncComponent(() => import('./forms/FieldRepeater.vue'))
    );
    app.component(
        'smart-autocomplete',
        defineAsyncComponent(() => import('./forms/SmartAutocomplete.vue'))
    );
    app.component(
        'app-phone-input',
        defineAsyncComponent(() => import('./forms/PhoneInput.vue'))
    );
    app.component(
        'app-phone-row',
        defineAsyncComponent(() => import('./forms/PhoneRow.vue'))
    );
    app.component(
        'ai-textarea',
        defineAsyncComponent(() => import('./forms/AiTextarea.vue'))
    );
    app.component(
        'rich-text-editor',
        defineAsyncComponent(() => import('./forms/RichTextEditor.vue'))
    );
    app.component('employee-autocomplete', EmployeeAutocomplete);
    app.component('app-taginput', AppTaginput);
    app.component(
        'employee-dropdown-filter',
        defineAsyncComponent(() => import('./forms/EmployeeDropdownFilter.vue'))
    );

    // Module-contributed components — each module registers its own via components(app)
    registerModuleComponents(app);
}
